import React, { useRef } from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";
import TryItButton from "../../../TryItButton";
import OutputBox from "../../../OutputBox";

export default function JS_Object_Methods() {
  const refs = {
    assign: useRef(null),
    keysValuesEntries: useRef(null),
    freezeSeal: useRef(null),
    create: useRef(null),
    hasOwn: useRef(null),
  };

  const scrollTo = (key) =>
    refs[key]?.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  const keyPoints = [
    { key: "assign", label: "Object.assign()" },
    {
      key: "keysValuesEntries",
      label: "Object.keys(), Object.values(), Object.entries()",
    },
    { key: "freezeSeal", label: "Object.freeze() and Object.seal()" },
    { key: "create", label: "Object.create()" },
    { key: "hasOwn", label: "Object.hasOwn()" },
  ];

  return (
    <div className="tutorial-container">
      <h1 className="tutorial-title">⚙️ JavaScript Object Methods</h1>
      <p className="tutorial-intro">
        JavaScript provides built-in <strong>Object methods</strong> to copy,
        freeze, inspect, and manipulate data easily.
      </p>

      {/* 🔑 Key Points Section */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef3c7, #fde68a)",
          padding: "1.5rem 2.5rem",
          borderRadius: "12px",
          marginTop: "1rem",
          marginBottom: "3rem",
        }}
      >
        <h2 style={{ color: "#075985", marginBottom: "1rem" }}>
          🔑 Key Points
        </h2>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "0.5rem",
          }}
        >
          {keyPoints.map((item) => (
            <p key={item.key} style={{ margin: "0.25rem 0" }}>
              <button
                onClick={() => scrollTo(item.key)}
                style={{
                  background: "transparent",
                  border: "none",
                  textDecoration: "underline",
                  color: "black",
                  cursor: "pointer",
                  fontSize: "1.05rem",
                }}
              >
                {item.label}
              </button>
            </p>
          ))}
        </div>
      </div>

      {/* 1️⃣ Object.assign() */}
      <div ref={refs.assign} className="tutorial-section">
        <h3>1️⃣ Object.assign()</h3>
        <p>
          The <code className="highlight_code">Object.assign()</code> method
          copies properties from one or more source objects to a target object.
        </p>
        <div className="code-demo-box read-only-box">
          <CodeCopyBlock
            code={`const obj1 = { a: 1, b: 2 };
const obj2 = { b: 3, c: 4 };
const result = Object.assign({}, obj1, obj2);
console.log(result);`}
          />
          <TryItButton
            code={`const obj1={a:1,b:2};const obj2={b:3,c:4};const result=Object.assign({},obj1,obj2);console.log(result);`}
          />
        </div>
        <OutputBox>Output: {"{ a: 1, b: 3, c: 4 }"}</OutputBox>
      </div>

      {/* 2️⃣ Object.keys(), Object.values(), Object.entries() */}
      <div ref={refs.keysValuesEntries} className="tutorial-section">
        <h3>2️⃣ Object.keys(), Object.values(), Object.entries()</h3>
        <p>
          These methods return arrays of property names, values, or key–value
          pairs of an object.
        </p>
        <div className="code-demo-box read-only-box">
          <CodeCopyBlock
            code={`const user = { name: "Alice", age: 25 };

console.log(Object.keys(user));   // ['name', 'age']
console.log(Object.values(user)); // ['Alice', 25]
console.log(Object.entries(user));// [['name', 'Alice'], ['age', 25]]`}
          />
          <TryItButton
            code={`const user={name:"Alice",age:25};console.log(Object.keys(user));console.log(Object.values(user));console.log(Object.entries(user));`}
          />
        </div>
        <OutputBox>
          Output:
          <pre>
            {`[ 'name', 'age' ]
[ 'Alice', 25 ]
[ [ 'name', 'Alice' ], [ 'age', 25 ] ]`}
          </pre>
        </OutputBox>
      </div>

      {/* 3️⃣ Object.freeze() and Object.seal() */}
      <div ref={refs.freezeSeal} className="tutorial-section">
        <h3>3️⃣ Object.freeze() and Object.seal()</h3>
        <p>
          <code className="highlight_code">Object.freeze()</code> makes an
          object fully immutable, while{" "}
          <code className="highlight_code">Object.seal()</code> prevents adding
          or deleting properties but allows changing existing ones.
        </p>
        <div className="code-demo-box read-only-box">
          <CodeCopyBlock
            code={`const obj = { name: "Bob" };
Object.freeze(obj);
obj.name = "Eve"; // No effect
console.log(obj.name);`}
          />
          <TryItButton
            code={`const obj={name:"Bob"};Object.freeze(obj);obj.name="Eve";console.log(obj.name);`}
          />
        </div>
        <OutputBox>Output: Bob</OutputBox>
      </div>

      {/* 4️⃣ Object.create() */}
      <div ref={refs.create} className="tutorial-section">
        <h3>4️⃣ Object.create()</h3>
        <p>
          The <code className="highlight_code">Object.create()</code> method
          creates a new object using another object as its prototype.
        </p>
        <div className="code-demo-box read-only-box">
          <CodeCopyBlock
            code={`const animal = { eats: true };
const dog = Object.create(animal);
dog.barks = true;

console.log(dog.eats, dog.barks);`}
          />
          <TryItButton
            code={`const animal={eats:true};const dog=Object.create(animal);dog.barks=true;console.log(dog.eats,dog.barks);`}
          />
        </div>
        <OutputBox>Output: true true</OutputBox>
      </div>

      {/* 5️⃣ Object.hasOwn() */}
      <div ref={refs.hasOwn} className="tutorial-section">
        <h3>5️⃣ Object.hasOwn()</h3>
        <p>
          The <code className="highlight_code">Object.hasOwn()</code> method
          checks if an object has a property as its *own* (not inherited).
        </p>
        <div className="code-demo-box read-only-box">
          <CodeCopyBlock
            code={`const person = { name: "Alice" };
console.log(Object.hasOwn(person, "name")); // true
console.log(Object.hasOwn(person, "age"));  // false`}
          />
          <TryItButton
            code={`const person={name:"Alice"};console.log(Object.hasOwn(person,"name"));console.log(Object.hasOwn(person,"age"));`}
          />
        </div>
        <OutputBox>
          Output:
          <pre>
            {`true
false`}
          </pre>
        </OutputBox>
      </div>

      {/* 🚀 Next Steps */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef9c3, #fde68a)",
          padding: "1rem 2rem",
          borderRadius: "12px",
          marginTop: "3rem",
        }}
      >
        <h2 style={{ color: "#92400e" }}>🚀 Next Steps</h2>
        <ul className="tutorial-list-points">
          <li>
            Try combining multiple Object methods for cloning and inspection.
          </li>
          <li>Learn about prototypes for inheritance-based object behavior.</li>
          <li>
            Experiment with{" "}
            <code className="highlight_code">Object.defineProperty()</code> for
            fine-grained control.
          </li>
        </ul>
      </div>
    </div>
  );
}
